import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {Alert, Checkbox, Empty, Flex, Modal, Tooltip, Typography} from 'antd'
import {CheckCircleFilled, RedoOutlined} from '@ant-design/icons'
import {PromoteChangesContext, SET_PROMOTE_FILES_DATA, SHOW_SELECT_FILES_POPUP,} from '../context/context'
import {NavContext} from '../../../context/global_context'
import { notifyError } from '../../../utils/notifications'
import {getRequest, postRequest} from '../../../services/base'
import InputText from '../../common/InputText'
import Button from '../../common/Button'
import EllipsisTooltip from '../../common/EllipsisTooltip'
import {ProgresSpinner} from '../../common/ProgresSpinner'
import useDebouncedValue from '../../../utils/common_function'
import {PUSH_TO_GIT} from '../../../constants/permission'
import {emptyFolders, FOLDER_KEYS, formatFolderLabel} from '../constants'
import BoxTabs from '../BoxTabs'

const { Text } = Typography

const SelectFilesModal = ({ getActiveBranchDetails = null }) => {
  const intl = useIntl()
  const [state, dispatch] = useContext(PromoteChangesContext)
  const [navState, navDispatch] = useContext(NavContext)
  const [modal, contextHolder] = Modal.useModal()

  const [searchValue, setSearchValue] = useState('')
  const debouncedSearch = useDebouncedValue(searchValue, 500)

  const [promotedFiles, setPromotedFiles] = useState([])
  const [promotedFolders, setPromotedFolders] = useState(emptyFolders())
  const [promotedCustomerFiles, setPromotedCustomerFiles] = useState([])
  const [includePn, setIncludePn] = useState(false)
  const [isReview, setReview] = useState(false)
  const [syncToGit, setSyncToGit] = useState(false)
  const [activeTab, setActiveTab] = useState(null)
  const [filesLoading, setFilesLoading] = useState(true)
  const fetchStartedRef = useRef(false)

  const vehicleData = state?.promote_files_data || {}
  const selectedVehicle = state?.selected_vehicle || {}

  // Per-section file lists, filtered by the debounced search and sorted.
  // Recomputed only when the underlying data or search term changes.
  const filteredSectionFiles = useMemo(() => {
    const result = {}
    FOLDER_KEYS.forEach((section) => {
      const files = isReview
        ? promotedFolders?.[section] || []
        : vehicleData?.section_data?.[section] || []
      const filtered = debouncedSearch
        ? files.filter((file_name) =>
            file_name.toLowerCase().includes(debouncedSearch.toLowerCase())
          )
        : files
      result[section] = [...filtered].sort()
    })
    return result
  }, [isReview, debouncedSearch, promotedFolders, vehicleData?.section_data])

  // O(1) membership lookups for the selected file grids.
  const promotedFoldersSets = useMemo(() => {
    const sets = {}
    FOLDER_KEYS.forEach((key) => {
      sets[key] = new Set(promotedFolders?.[key] || [])
    })
    return sets
  }, [promotedFolders])

  const promotedFilesSet = useMemo(
    () => new Set(promotedFiles),
    [promotedFiles]
  )
  const promotedCustomerFilesSet = useMemo(
    () => new Set(promotedCustomerFiles),
    [promotedCustomerFiles]
  )

  useEffect(() => {
    if (!state?.show_select_files_popup) {
      fetchStartedRef.current = false
      return
    }
    if (fetchStartedRef.current) return
    fetchStartedRef.current = true
    getMakeModalFileDetails()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.show_select_files_popup])

  useEffect(() => {
    if (!state?.show_select_files_popup) return
    const firstSelectedFolder = FOLDER_KEYS.find(
      (key) => promotedFolders?.[key]?.length > 0
    )
    setActiveTab(firstSelectedFolder || null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReview])

  const getMakeModalFileDetails = async () => {
    setFilesLoading(true)

    try {
      let res = await getRequest(
        `/api/promote_changes/vehicle-details/${selectedVehicle?.vehicle_id}/`
      )
      if (res?.status === false) {
        notifyError(navDispatch, intl.formatMessage({ id: 'promoteChanges.detailsLoadError' }))
        return
      }
      dispatch({ type: SET_PROMOTE_FILES_DATA, data: res?.data })

      if (!res?.data?.make_model_exists) {
        setPromotedCustomerFiles([
          ...Object.keys(res?.data?.customer_files || {}),
        ])
      }
      setIncludePn(res?.data?.include_pn)

      selectDefaultSavedFiles(res.data)
    } finally {
      setFilesLoading(false)
    }
  }

  const selectDefaultSavedFiles = (responseData) => {
    let tempPromotedFolders = emptyFolders()
    let tempPromotedCustomerFiles = []
    let tempPromotedFiles = []

    Object.keys(responseData?.customer_files || {}).forEach(
      (customer_file_key) => {
        if (responseData?.customer_files?.[customer_file_key]?.is_exists) {
          tempPromotedCustomerFiles.push(customer_file_key)
        }
      }
    )
    setPromotedCustomerFiles(tempPromotedCustomerFiles)

    Object.keys(responseData?.files || {}).forEach((file_key) => {
      if (responseData?.files?.[file_key]?.is_exists) {
        tempPromotedFiles.push(file_key)
      }
    })
    setPromotedFiles(tempPromotedFiles)

    Object.keys(responseData?.folder_data || {}).forEach((folder_key) => {
      responseData?.folder_data?.[folder_key].forEach((folder_dt) => {
        tempPromotedFolders[folder_key].push(folder_dt.file_name)
      })
    })
    setPromotedFolders(tempPromotedFolders)
  }

  const selectUnSelectFiles = (select, file_name, section_name = null) => {
    if (FOLDER_KEYS.includes(section_name)) {
      setPromotedFolders((prev) => {
        const temp = { ...prev }
        const idx = temp[section_name].indexOf(file_name)
        if (select && idx === -1) {
          temp[section_name] = [...temp[section_name], file_name]
        } else if (!select && idx > -1) {
          temp[section_name] = temp[section_name].filter(
            (name) => name !== file_name
          )
        }
        return temp
      })
    } else if (section_name === 'customer_data') {
      setPromotedCustomerFiles((prev) =>
        select && !prev.includes(file_name)
          ? [...prev, file_name]
          : prev.filter((name) => name !== file_name)
      )
    } else {
      setPromotedFiles((prev) =>
        select && !prev.includes(file_name)
          ? [...prev, file_name]
          : prev.filter((name) => name !== file_name)
      )
    }
  }

  const selectAllValues = (is_checked, section) => {
    setPromotedFolders((prev) => ({
      ...prev,
      [section]: is_checked
        ? [...(vehicleData?.section_data?.[section] || [])]
        : [],
    }))
  }

  const submitReviewedData = async () => {
    modal.confirm({
      title: intl.formatMessage({
        id: 'promoteChanges.submitReviewedFilesTitle',
      }),
      content: intl.formatMessage({
        id: 'promoteChanges.submitReviewedFilesConfirm',
      }),
      centered: true,
      maskClosable: false,
      okText: intl.formatMessage({ id: 'promoteChanges.submit' }),
      cancelText: intl.formatMessage({ id: 'promoteChanges.cancel' }),
      onOk: async () => {
        navDispatch({
          type: 'loader',
          data: {
            caption: intl.formatMessage({ id: 'promoteChanges.promotingData' }),
            showLoader: true,
          },
          notification: {},
        })

        try {
          let payload = {
            promoted_files: promotedFiles,
            promoted_folders: promotedFolders,
            sync_to_server: syncToGit,
            customer_files: promotedCustomerFiles,
            include_pn: includePn,
          }
          let res = await postRequest(
            `/api/promote_changes/files-folder/submit/${selectedVehicle?.vehicle_id}/`,
            payload
          )
          if (res?.status) {
            navDispatch({
              type: 'notification',
              data: {
                notification_type: 'success',
                msg: intl.formatMessage({ id: 'promoteChanges.filePromoted' }),
              },
            })
          } else {
            navDispatch({
              type: 'notification',
              data: {
                notification_type: 'error',
                msg: intl.formatMessage({
                  id: 'promoteChanges.failedToUpload',
                }),
              },
            })
          }
          resetSelections()
          dispatch({
            type: SHOW_SELECT_FILES_POPUP,
            data: false,
            selectedVehicle: null,
            showVehicleModal: false,
            selectedFiles: [],
          })
          if (getActiveBranchDetails) getActiveBranchDetails()
        } finally {
          navDispatch({
            type: 'loader',
            data: { showLoader: false },
            notification: {},
          })
        }
      },
    })
  }

  const resetSelections = () => {
    setPromotedFiles([])
    setPromotedFolders(emptyFolders())
    setPromotedCustomerFiles([])
    setActiveTab(null)
    dispatch({ type: SET_PROMOTE_FILES_DATA, data: [] })
    setReview(false)
    setSyncToGit(false)
  }

  const clickToReview = () => {
    setReview(true)
    const folderKey = FOLDER_KEYS.find(
      (key) => promotedFolders?.[key]?.length > 0
    )
    setActiveTab(folderKey || null)
  }

  const closeModal = () => {
    dispatch({
      type: SHOW_SELECT_FILES_POPUP,
      data: false,
      selectedVehicle: {},
      showVehicleModal: false,
      selectedFiles: {},
    })
  }

  const renderCheckableFiles = (filesMap, sectionName) => {
    const keys = Object.keys(filesMap || {})
    if (keys.length === 0) {
      return (
        <Flex
          align="center"
          justify="center"
          className="promote-empty-state-sm"
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={intl.formatMessage({ id: 'promoteChanges.noRecords' })}
          />
        </Flex>
      )
    }
    return (
      <Flex wrap="wrap" gap={8} className="promote-files-grid">
        {keys.map((key) => (
          <div
            key={`${sectionName}-${key}`}
            className="promote-file-check-item"
          >
            <Checkbox
              checked={
                sectionName === 'customer_data'
                  ? promotedCustomerFilesSet.has(key)
                  : promotedFilesSet.has(key)
              }
              onChange={(e) =>
                selectUnSelectFiles(e.target.checked, key, sectionName)
              }
            >
              <Flex align="center" gap={5} className="promote-file-label">
                {filesMap?.[key]?.is_exists ? (
                  <Tooltip
                    title={intl.formatMessage(
                      { id: 'promoteChanges.fileExistsTooltip' },
                      { fileName: key }
                    )}
                  >
                    <Text
                      ellipsis
                      className="promote-file-label promote-file-label-exists"
                    >
                      {key}
                    </Text>
                  </Tooltip>
                ) : (
                  <Text
                    ellipsis={{ tooltip: key }}
                    className="promote-file-label"
                  >
                    {key}
                  </Text>
                )}
              </Flex>
            </Checkbox>
          </div>
        ))}
      </Flex>
    )
  }

  const renderFolderSection = (section) => {
    const filteredFiles = filteredSectionFiles[section] || []
    return (
      <Flex vertical gap={12} className="promote-tab-pane">
        <Flex align="center" gap={16} wrap>
          {isReview && section === 'connector_views' && (
            <Text type="secondary">
              {intl.formatMessage(
                { id: 'promoteChanges.includedPartNumber' },
                { value: includePn ? 'Yes' : 'No' }
              )}
            </Text>
          )}
          {!isReview && vehicleData?.section_data?.[section]?.length > 0 && (
            <Checkbox
              checked={
                vehicleData?.section_data?.[section]?.length > 0 &&
                vehicleData?.section_data?.[section]?.length ===
                  (promotedFolders?.[section]?.length || 0)
              }
              onChange={(e) => selectAllValues(e.target.checked, section)}
            >
              {intl.formatMessage({ id: 'promoteChanges.selectAll' })}
            </Checkbox>
          )}
          {!isReview && vehicleData?.section_data?.[section]?.length > 0 && (
            <Text type="secondary">
              {intl.formatMessage(
                { id: 'promoteChanges.selectedCount' },
                {
                  selected: promotedFolders?.[section]?.length || 0,
                  total: vehicleData?.section_data?.[section]?.length || 0,
                }
              )}
            </Text>
          )}
          {!isReview && section === 'connector_views' && (
            <Checkbox
              checked={includePn}
              onChange={(e) => setIncludePn(e.target.checked)}
            >
              {intl.formatMessage({
                id: 'promoteChanges.includePartNumber',
              })}
            </Checkbox>
          )}
          <InputText
            name={`search_${section}`}
            placeholder={intl.formatMessage(
              { id: 'promoteChanges.searchSection' },
              { section }
            )}
            type="search"
            value={searchValue}
            isRequired={false}
            fieldless
            onChange={(value) => setSearchValue(value)}
            wrapperClass="promote-section-search"
          />
        </Flex>
        <div className="promote-section-files-list">
          <Flex wrap="wrap" gap={8} className="promote-files-grid">
            {filteredFiles.map((file_name, idx) => (
              <div
                key={`${section}-${file_name}-${idx}`}
                className="promote-file-check-item"
              >
                {isReview ? (
                  <>
                    <CheckCircleFilled className="promote-check-icon" />
                    <EllipsisTooltip className="ellipsis">
                      {file_name}
                    </EllipsisTooltip>
                  </>
                ) : (
                  <Checkbox
                    checked={promotedFoldersSets[section]?.has(file_name)}
                    onChange={(e) =>
                      selectUnSelectFiles(e.target.checked, file_name, section)
                    }
                  >
                    <EllipsisTooltip className="ellipsis">
                      {file_name}
                    </EllipsisTooltip>
                  </Checkbox>
                )}
              </div>
            ))}
          </Flex>
          {filteredFiles.length === 0 && (
            <Flex
              align="center"
              justify="center"
              className="promote-empty-state"
            >
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={intl.formatMessage({
                  id:
                    isReview && promotedFolders?.[section]?.length === 0
                      ? 'promoteChanges.noFilesSelected'
                      : 'promoteChanges.noRecords',
                })}
              />
            </Flex>
          )}
        </div>
      </Flex>
    )
  }

  const renderSelectView = () => (
    <Flex gap={16} className="promote-select-columns">
      <Flex vertical gap={16} className="promote-select-left">
        <div className="promote-section-block promote-section-block-customer">
          <div className="promote-section-header">
            <Flex align="center" justify="space-between" gap={16}>
              <Text strong>
                {intl.formatMessage({
                  id: 'promoteChanges.customerSetupHeader',
                })}
              </Text>
            </Flex>
            <Text type="secondary" className="promote-section-hint">
              {intl.formatMessage({ id: 'promoteChanges.requiredFilesNote' })}
            </Text>
          </div>
          <div className="promote-section-scroll">
            {renderCheckableFiles(vehicleData?.customer_files, 'customer_data')}
          </div>
        </div>

        <div className="promote-section-block">
          <div className="promote-section-header">
            <Text strong>
              {intl.formatMessage({
                id: 'promoteChanges.makeModelFilesHeader',
              })}
            </Text>
          </div>
          <div className="promote-section-scroll">
            {renderCheckableFiles(vehicleData?.files, null)}
          </div>
        </div>
      </Flex>

      {vehicleData?.folders && (
        <div className="promote-select-right">
          <BoxTabs
            options={FOLDER_KEYS.map((key) => ({
              label: formatFolderLabel(key),
              value: key,
            }))}
            value={activeTab || FOLDER_KEYS[0]}
            onChange={(e) => setActiveTab(e.target.value)}
          />
          {renderFolderSection(activeTab || FOLDER_KEYS[0])}
        </div>
      )}
    </Flex>
  )

  const renderReviewSummary = () => (
    <Flex gap={16} className="promote-select-columns">
      <Flex vertical gap={16} className="promote-select-left">
        <div className="promote-section-block promote-section-block-customer">
          <div className="promote-section-header">
            <Text strong>
              {intl.formatMessage({ id: 'promoteChanges.customerSetupHeader' })}
            </Text>
            <Text type="secondary" className="promote-section-hint">
              {intl.formatMessage({ id: 'promoteChanges.requiredFilesNote' })}
            </Text>
          </div>
          <div className="promote-section-scroll">
            {promotedCustomerFiles?.length > 0 ? (
              <Flex wrap="wrap" gap={8} className="promote-files-grid">
                {promotedCustomerFiles.map((key, idx) => (
                  <div
                    key={`review-customer-${key}-${idx}`}
                    className="promote-file-check-item"
                  >
                    {vehicleData?.customer_files?.[key]?.is_exists ? (
                      <Tooltip
                        title={intl.formatMessage(
                          { id: 'promoteChanges.fileExistsTooltip' },
                          { fileName: key }
                        )}
                      >
                        <Text
                          ellipsis
                          className="promote-file-label promote-file-label-exists"
                        >
                          {key}
                        </Text>
                      </Tooltip>
                    ) : (
                      <Text
                        ellipsis={{ tooltip: key }}
                        className="promote-file-label"
                      >
                        {key}
                      </Text>
                    )}
                  </div>
                ))}
              </Flex>
            ) : (
              <Flex
                align="center"
                justify="center"
                className="promote-empty-state"
              >
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={intl.formatMessage({
                    id: 'promoteChanges.noRecords',
                  })}
                />
              </Flex>
            )}
          </div>
        </div>

        <div className="promote-section-block">
          <div className="promote-section-header">
            <Text strong>
              {intl.formatMessage({
                id: 'promoteChanges.makeModelFilesHeader',
              })}
            </Text>
          </div>
          <div className="promote-section-scroll">
            {promotedFiles?.length > 0 ? (
              <Flex wrap="wrap" gap={8} className="promote-files-grid">
                {promotedFiles.map((key, idx) => (
                  <div
                    key={`review-file-${key}-${idx}`}
                    className="promote-file-check-item"
                  >
                    {vehicleData?.files?.[key]?.is_exists ? (
                      <Tooltip
                        title={intl.formatMessage(
                          { id: 'promoteChanges.fileExistsTooltip' },
                          { fileName: key }
                        )}
                      >
                        <Text
                          ellipsis
                          className="promote-file-label promote-file-label-exists"
                        >
                          {key}
                        </Text>
                      </Tooltip>
                    ) : (
                      <Text
                        ellipsis={{ tooltip: key }}
                        className="promote-file-label"
                      >
                        {key}
                      </Text>
                    )}
                  </div>
                ))}
              </Flex>
            ) : (
              <Flex
                align="center"
                justify="center"
                className="promote-empty-state"
              >
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={intl.formatMessage({
                    id: 'promoteChanges.noRecords',
                  })}
                />
              </Flex>
            )}
          </div>
        </div>
      </Flex>

      <div className="promote-select-right">
        <BoxTabs
          options={FOLDER_KEYS.map((key) => ({
            label: formatFolderLabel(key),
            value: key,
          }))}
          value={activeTab || FOLDER_KEYS[0]}
          onChange={(e) => setActiveTab(e.target.value)}
        />
        {renderFolderSection(activeTab || FOLDER_KEYS[0])}
      </div>
    </Flex>
  )

  let modalTitle = intl.formatMessage({ id: 'promoteChanges.selectFilesTitle' })
  if (isReview) {
    modalTitle = intl.formatMessage({
      id: 'promoteChanges.reviewAndSubmitTitle',
    })
  }

  return (
    <>
      {contextHolder}
      <Modal
        title={`${modalTitle}${isReview ? '' : ` ${selectedVehicle?.name || ''}`}`}
        open={state.show_select_files_popup}
        centered={true}
        maskClosable={false}
        onCancel={closeModal}
        width="90%"
        footer={null}
        className="promote-changes-select-files-modal"
        destroyOnHidden
      >
        {filesLoading ? (
          <Flex
            align="center"
            justify="center"
            className="promote-status-wrapper"
          >
            <ProgresSpinner
              caption={intl.formatMessage({
                id: 'promoteChanges.loadingVehicleData',
              })}
            />
          </Flex>
        ) : vehicleData?.missing_files?.length > 0 ? (
          <Flex
            align="center"
            justify="center"
            vertical
            gap={8}
            className="promote-empty-state"
          >
            <Alert
              type="error"
              showIcon
              message={intl.formatMessage({
                id: 'promoteChanges.missingFilesError',
              })}
              description={
                <Flex vertical>
                  <Text>{vehicleData?.missing_files.join(', ')}</Text>
                  <Text>
                    {intl.formatMessage({
                      id: 'promoteChanges.missingFilesDescription',
                    })}
                  </Text>
                </Flex>
              }
            />
          </Flex>
        ) : vehicleData?.is_processing ? (
          <Flex
            align="center"
            justify="center"
            className="promote-status-wrapper"
          >
            <ProgresSpinner
              caption={intl.formatMessage({
                id: 'promoteChanges.syncingInProgress',
              })}
            />
          </Flex>
        ) : (
          <Flex vertical gap={16}>
            <div className="promote-select-body">
              {isReview ? renderReviewSummary() : renderSelectView()}
            </div>

            <Flex
              align="center"
              justify="flex-end"
              gap={16}
              wrap
              className="promote-modal-footer"
            >
              {isReview ? (
                <Flex align="center" gap={12} wrap>
                  {navState?.permissions?.includes(PUSH_TO_GIT) && (
                    <Checkbox
                      checked={syncToGit}
                      onChange={(e) => setSyncToGit(e.target.checked)}
                    >
                      {intl.formatMessage({ id: 'promoteChanges.syncToGit' })}
                    </Checkbox>
                  )}
                  <Button onClick={() => setReview(false)}>
                    {intl.formatMessage({ id: 'promoteChanges.reSelectFiles' })}
                  </Button>
                  <Button type="primary" onClick={submitReviewedData}>
                    {intl.formatMessage({
                      id: 'promoteChanges.confirmAndSubmit',
                    })}
                  </Button>
                </Flex>
              ) : (
                <Flex
                  align="center"
                  justify="space-between"
                  style={{ width: '100%' }}
                >
                  <Button
                    icon={<RedoOutlined />}
                    onClick={getMakeModalFileDetails}
                  >
                    {intl.formatMessage({ id: 'promoteChanges.refresh' })}
                  </Button>
                  <Button onClick={clickToReview}>
                    {intl.formatMessage({ id: 'promoteChanges.review' })}
                  </Button>
                </Flex>
              )}
            </Flex>
          </Flex>
        )}
      </Modal>
    </>
  )
}

export default SelectFilesModal
